
import assetsApi from '../../../../api/assets.api';
import CreateStepModal from './create-step-modal';
import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Table, Button } from '@weirgroup/weir-dls';
import { getTableHeaders } from '../../../../utils/table.util';
import { useTranslation } from 'react-i18next';
import { useGlobalError } from '../../../../contexts/global-error.context';
import { AssetRow } from '../../../../types/assetTypes';
import { StorageService, ACCESS_TKN_KEY, REFRESH_TOKEN } from '../../../../services/storage_service';
import './create-step.scss';
import { useAnalyticsEvent } from '../../../../hooks/useAnalyticsEvent';

interface CreateStepProps {
    darkMode: boolean;
    creationNumber: string;
    fileName: string;
    businessEntities: string;
    assets: AssetRow[];
    onNext: (result: { successCount: number; failCount: number; failedItems?: { serialNumber: string; error: any; asset: AssetRow }[] }) => void;
    onPrev: () => void;
}

export const CreateStep: React.FC<CreateStepProps> = ({
    darkMode,
    creationNumber,
    fileName,
    businessEntities,
    assets,
    onNext,
    onPrev,
}) => {
    const logAnalyticsEvent = useAnalyticsEvent();
    const { t } = useTranslation();
    const { showError } = useGlobalError();
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [progress, setProgress] = useState(0);
    const [continueDisabled, setContinueDisabled] = useState(true);
    const [successCount, setSuccessCount] = useState(0);
    const [errorCount, setErrorCount] = useState(0);
    const [rowCount, setRowCount] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        logAnalyticsEvent('web_user_viewed_create_step', { description: 'User viewed the create step in upload assets' });
    },[]);

    // Table headers (could be dynamic)
    // Helper to add spaces between words in header keys (e.g., 'ASSETTYPE' -> 'ASSET TYPE', 'serialNumber' -> 'SERIAL NUMBER')
    const formatHeaderLabel = (key: string) => {
        // Limit input length to prevent ReDoS
        const safeKey = key.slice(0, 100);
        // Insert space before capital letters, replace underscores, and uppercase
        return safeKey
            .replace(/([A-Z])/g, ' $1')
            .replace(/_/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .toUpperCase();
    };
    const tableHeaders = useMemo(() => {
        
        if (assets.length === 0) return [];
        return Object.keys(assets[0]).map((key) => ({ label: formatHeaderLabel(key), key }));
    }, [assets]);

    // Pagination logic
    const pagedData = useMemo(() => {
        if (!assets.length) return [];
        const pages: AssetRow[][] = [];
        for (let i = 0; i < assets.length; i += rowCount) {
            pages.push(assets.slice(i, i + rowCount));
        }
        return pages;
    }, [assets, rowCount]);

    // Helper: Build ESCO payload
    const buildEscoPayload = (asset: AssetRow) => {
        const payload: any = {
            tag_id: asset.tagId,
            serial_number: asset.serialNumber,
            tag_type: 'satellite',
            is_bulk: true,
        };
        if (asset.lipWidth) payload.lip_width = asset.lipWidth;
        if (asset.class) payload.class = asset.class;
        if (asset.capacity) payload.capacity = asset.capacity;
        if (asset.equipmentType) payload.equipment_type = asset.equipmentType;
        if (asset.attachmentType) payload.attachment_type = asset.attachmentType;
        if (asset.equipmentModel) payload.equipment_model = asset.equipmentModel;
        if (asset.stockNumber) payload.stock_number = asset.stockNumber;
        if (asset.machineNumber) payload.machine_number = asset.machineNumber;
        if (asset.latitude) payload.latitude = btoa(String(asset.latitude));
        if (asset.longitude) payload.longitude = btoa(String(asset.longitude));
        if (asset.weight) payload.weight = asset.weight;
        if (asset.make) payload.make = asset.make;
        return payload;
    };

    // Helper: Build Non-ESCO payload
    const buildNonEscoPayload = (asset: AssetRow) => {
        const payload: any = {
            tag_id: asset.tagId,
            serial_number: asset.serialNumber,
            tag_type: asset.tagType || 'satellite',
            is_bulk: true,
            make:asset.make,
            weight: Number(asset.weight)
        };
        if (asset.equipmentType) payload.equipment_type = asset.equipmentType;
        if (asset.attachmentType) payload.attachment_type = asset.attachmentType;
        if (asset.equipmentModel) payload.equipment_model = asset.equipmentModel;
        if (asset.manufacturer) payload.manufacturer = asset.manufacturer;
        if (asset.lipWidth) payload.lip_width = asset.lipWidth;
        if (asset.class) payload.class = asset.class;
        if (asset.capacity) payload.capacity = asset.capacity;
        if (asset.stockNumber) payload.stock_number = asset.stockNumber;
        if (asset.machineNumber) payload.machine_number = asset.machineNumber;
        if (asset.latitude) payload.latitude = btoa(String(asset.latitude));
        if (asset.longitude) payload.longitude = btoa(String(asset.longitude));
        if (asset.files?.length) payload.files = asset.files;
        return payload;
    };

    // Helper: Convert payload to FormData
    const payloadToFormData = (payload: any) => {
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                if (key === 'files' && value instanceof FileList) {
                    Array.from(value).forEach((file: File) => formData.append('files', file));
                } else if (key === 'files' && Array.isArray(value)) {
                    value.forEach((file: File) => formData.append('files', file));
                } else if (typeof value === 'object' && !(value instanceof Date) && !(value instanceof File)) {
                    // Only stringify plain objects, not Dates, Files, or Arrays
                    if (Object.prototype.toString.call(value) === '[object Object]') {
                        formData.append(key, JSON.stringify(value));
                    } else {
                        // Skip stringifying arrays or other objects
                        formData.append(key, '');
                    }
                } else {
                    formData.append(key, String(value));
                }
            }
        });
        return formData;
    };

    // Helper: Process a single asset
    const processAsset = async (asset: AssetRow, accessToken: string, refreshToken: string) => {
        if (asset.assetType && asset.assetType.toLowerCase() === 'esco') {
            const escoPayload = buildEscoPayload(asset);
            await assetsApi.createEscoAsset(escoPayload, { accessToken, refreshToken });
        } else {
            const nonEscoPayload = buildNonEscoPayload(asset);
            const formData = payloadToFormData(nonEscoPayload);
            // @ts-ignore: Accept FormData for backward compatibility
            await assetsApi.createNonEscoAsset(formData, { accessToken, refreshToken });
        }
    };

    // Helper: Retry logic for asset creation
    const tryProcessAsset = async (asset: AssetRow, accessToken: string, refreshToken: string, showError: (msg: string) => void, t: any) => {
        let attempt = 0;
        let success = false;
        let error: any = null;
        while (attempt < 3 && !success) {
            try {
                await processAsset(asset, accessToken, refreshToken);
                success = true;
            } catch (e) {
                error = e;
                attempt++;
                if (attempt === 3) {
                    showError(t('uploadAssets.createStep.assetCreationFailed', { error: String(e) }));
                }
            }
        }
        return { success, error };
    };

    // Asset creation logic
    const handleCreate = async () => {
    setLoading(true);
    setModalOpen(true);
    setProgress(0);
    setSuccessCount(0);
    setErrorCount(0);
    setContinueDisabled(true);
        let successCount = 0;
        let failCount = 0;
        const accessToken = StorageService.getItem(ACCESS_TKN_KEY) || '';
        const refreshToken = StorageService.getItem(REFRESH_TOKEN) || '';

        for (let i = 0; i < assets.length; i++) {
            const asset = assets[i];
            const { success } = await tryProcessAsset(asset, accessToken, refreshToken, showError, t);
            if (success) {
                successCount++;
            } else {
                failCount++;
                // failedItems removed for SonarLint compliance
            }
            setProgress(i + 1);
            setSuccessCount(successCount)
            setErrorCount(failCount);
        }
        
        // Call update-statuses API before finishing loading
        try {
            const serialNumbers = assets.map((a) => a.serialNumber);
            const accessToken = StorageService.getItem(ACCESS_TKN_KEY) || '';
            const refreshToken = StorageService.getItem(REFRESH_TOKEN) || '';

            setTimeout(() => {
                assetsApi.updateAssetStatuses({ serial_numbers: serialNumbers.join(',') }, { accessToken, refreshToken }).then(() => {
                    console.log('Asset statuses updated successfully');
                });
            
                setLoading(false);
                setContinueDisabled(false);
            }, 3000);
            logAnalyticsEvent('web_user_created_an_asset', { description: 'User created an asset' });
        } catch (e) {
            showError(t('uploadAssets.createStep.assetStatusUpdateFailed', { error: String(e) }));
        }
    };

    // Handler for continue button
    const handleContinue = () => {
        setModalOpen(false);
        onNext({ successCount: successCount, failCount: errorCount }); // You may want to pass actual counts if needed

    };
    return (
        <div className="create-step-container">
            <div className="create-step-summary-table">
                <Table
                    darkMode={darkMode}
                    header={getTableHeaders(['creation_number', 'file_name', 'assets'])}
                    source={[{
                        creation_number: creationNumber,
                        file_name: fileName,
                        assets: businessEntities,
                    }]}
                    showLoader={false}
                />
            </div>
            <div className="create-step-table-view" ref={containerRef}>
                {(pagedData.length > 0 && tableHeaders.length > 0) ? (
                    <Table
                        darkMode={darkMode}
                        header={tableHeaders}
                        source={pagedData.length === 1 ? pagedData[0] : pagedData[currentPage - 1] || []}
                        pagination
                        rowsPerPageDropdownOptions={[
                            { name: '5', value: 5 },
                            { name: '10', value: 10 },
                            { name: '15', value: 15 },
                            { name: '25', value: 25 },
                            { name: '50', value: 50 },
                        ]}
                        onRowsPerPageDropdownChange={setRowCount}
                        onPageChange={setCurrentPage}
                        rowsPerPageDropdownValue={rowCount}
                        currentPage={currentPage}
                        maxPages={pagedData.length}
                        showLoader={false}
                    />
                ) : null}
            </div>
            <div className="create-step-next-btn-section">
                <Button
                    darkMode={darkMode}
                    label={t('uploadAssets.buttons.prev', 'Prev')}
                    size="md"
                    variant="secondary"
                    onClick={onPrev}
                    className="create-step-btn"
                    disabled={loading}
                />
                <Button
                    darkMode={darkMode}
                    label={loading ? t('uploadAssets.buttons.creating', 'Creating...') : t('uploadAssets.buttons.create', 'Create')}
                    size="md"
                    variant="primary"
                    onClick={handleCreate}
                    className="create-step-create-btn"
                    disabled={loading}
                />
            </div>
            {/* Success and error handled by global notification system */}
            <CreateStepModal
                open={modalOpen}
                current={progress}
                total={assets.length}
                onClose={handleContinue}
                errorCount={errorCount}
                continueDisabled={continueDisabled}
            />
        </div>
    );
};
